import { useState, useCallback, useEffect, useMemo } from 'react';
import { EventData, EVENTS, STATUS, Step } from 'react-joyride';

const TOUR_KEY = 'solimat-appointments-tour-v1';

const BASE_STEPS: Step[] = [
  {
    target: '.tabs',
    title: 'Próximas y pasadas',
    content: 'Cambia entre las pestañas "Próximas" para ver tus citas pendientes, y "Pasadas" para consultar el historial de citas ya realizadas.',
    placement: 'bottom',
    skipBeacon: true,
  },
];

const APPOINTMENT_STEPS: Step[] = [
  {
    target: '.appts__toolbar',
    title: 'Ordenar citas',
    content: 'Usa el botón de ordenación para cambiar el orden de las citas entre fecha más reciente o más antigua.',
    placement: 'bottom',
    skipBeacon: true,
  },
  {
    target: '.appts',
    title: 'Listado de citas',
    content: 'Aquí se muestran todas tus citas de la pestaña activa. Haz clic en cualquier tarjeta para ver el detalle completo.',
    placement: 'top',
    skipBeacon: true,
  },
  {
    target: '.appt',
    title: 'Tarjeta de cita',
    content: 'Cada tarjeta muestra la fecha, especialidad, médico, centro asistencial y el estado actual de la cita.',
    placement: 'right',
    skipBeacon: true,
  },
  {
    target: '.appt__date',
    title: 'Fecha de la cita',
    content: 'El bloque de fecha indica el día y mes de la cita de forma visual para localizarla rápidamente.',
    placement: 'right',
    skipBeacon: true,
  },
  {
    target: '.appt__body',
    title: 'Detalle de la cita',
    content: 'Aquí se muestra la especialidad, el nombre del médico y el centro asistencial donde se realizará la cita.',
    placement: 'left',
    skipBeacon: true,
  },
  {
    target: '.pill',
    title: 'Estado de la cita',
    content: 'La etiqueta de estado indica si la cita está solicitada, confirmada, realizada, reprogramada u otros estados posibles.',
    placement: 'top',
    skipBeacon: true,
  },
];

export const useAppointmentsTour = (hasAppointments: boolean = false) => {
  const [run, setRun] = useState(false);
  const [tourKey, setTourKey] = useState(0);

  const steps = useMemo<Step[]>(
    () => (hasAppointments ? [...BASE_STEPS, ...APPOINTMENT_STEPS] : BASE_STEPS),
    [hasAppointments],
  );

  const handleCallback = useCallback((data: EventData) => {
    const { type, status, index } = data as any;
    const finished = status === STATUS.FINISHED || status === STATUS.SKIPPED;

    if (finished || (type as string) === EVENTS.TOUR_END) {
      setRun(false);
      localStorage.setItem(TOUR_KEY, 'true');
      window.dispatchEvent(new CustomEvent('solimat:tourStateChange', { detail: { active: false } }));
    }

    if ((type as string) === EVENTS.STEP_AFTER && index === 0) {
      window.dispatchEvent(new CustomEvent('solimat:tourStateChange', { detail: { active: false } }));
    }
  }, []);

  const startTour = useCallback(() => {
    setRun(current => {
      if (current) return false;
      setTourKey(k => k + 1);
      return true;
    });
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('solimat:tourStateChange', { detail: { active: run } }));
  }, [run]);

  return { run, tourKey, steps, handleCallback, startTour };
};
